'use client'

import React, { memo, useState } from 'react'

import {
  AccountCircle,
  Circle,
  Close,
  DarkMode,
  Flag,
  LightMode,
  LiveHelp,
  Logout,
  Menu as MenuIcon,
  Notifications,
  Settings,
  Support,
  Translate,
} from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  AppBar as MuiAppBar,
  Select,
  Stack,
  Toolbar,
  Typography,
  alpha,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from '@mui/material'

import { createTransition } from '../../../utils/animations'
import { APP_CONFIG, LANGUAGES } from '../../../utils/constants'

export interface AppBarProps {
  variant?: 'public' | 'authenticated' | 'admin'
  showLanguageSelector?: boolean
  showNotifications?: boolean
  userName?: string
  userRole?: string
  systemStatus?: 'operational' | 'maintenance' | 'degraded'
  onLogout?: () => void
  onProfileClick?: () => void
  onThemeToggle?: () => void
  isDarkMode?: boolean
}

const AppBar = memo<AppBarProps>(
  ({
    variant = 'public',
    showLanguageSelector = true,
    showNotifications = false,
    userName,
    userRole,
    systemStatus = 'operational',
    onLogout,
    onProfileClick,
    onThemeToggle,
    isDarkMode = false,
  }) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const isTablet = useMediaQuery(theme.breakpoints.down('md'))
    const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 100 })

    const [language, setLanguage] = useState('en')
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

    const getStatusColor = () => {
      switch (systemStatus) {
        case 'operational':
          return theme.palette.success.main
        case 'maintenance':
          return theme.palette.warning.main
        case 'degraded':
          return theme.palette.error.main
        default:
          return theme.palette.success.main
      }
    }

    const getStatusLabel = () => {
      switch (systemStatus) {
        case 'operational':
          return 'All Systems Operational'
        case 'maintenance':
          return 'Scheduled Maintenance'
        case 'degraded':
          return 'Degraded Performance'
        default:
          return 'System Status'
      }
    }

    return (
      <>
        <MuiAppBar
          position="fixed"
          elevation={trigger ? 2 : 0}
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            backdropFilter: 'blur(10px)',
            bgcolor: alpha(theme.palette.background.default, 0.9),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            ...createTransition(['box-shadow', 'background-color']),
          }}
        >
          <Container maxWidth="xl">
            <Toolbar
              disableGutters
              sx={{
                height: { xs: 56, sm: trigger ? 60 : 64 },
                ...createTransition('height'),
              }}
            >
              {/* Logo Section */}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 0 }}>
                <Box
                  sx={{
                    width: { xs: 36, sm: trigger ? 36 : 40 },
                    height: { xs: 36, sm: trigger ? 36 : 40 },
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: theme.shadows[3],
                    ...createTransition(['width', 'height']),
                  }}
                >
                  <Flag
                    sx={{
                      fontSize: { xs: 20, sm: trigger ? 20 : 24 },
                      color: 'white',
                      ...createTransition('font-size'),
                    }}
                  />
                </Box>

                {!isMobile && (
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {APP_CONFIG.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {variant === 'admin'
                        ? 'Administrative Portal'
                        : variant === 'authenticated'
                          ? 'Staff Portal'
                          : 'Federal Operations Portal'}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Desktop Actions */}
              {!isTablet && (
                <Stack direction="row" spacing={2} alignItems="center">
                  {/* System Status */}
                  {variant !== 'public' && (
                    <Chip
                      icon={<Circle sx={{ fontSize: 10, color: getStatusColor() }} />}
                      label={getStatusLabel()}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: alpha(getStatusColor(), 0.1),
                        color: getStatusColor(),
                        border: `1px solid ${alpha(getStatusColor(), 0.2)}`,
                      }}
                    />
                  )}

                  {/* Language Selector */}
                  {showLanguageSelector && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        startAdornment={<Translate sx={{ mr: 1, fontSize: 20 }} />}
                        sx={{
                          '& .MuiSelect-select': {
                            py: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                      >
                        {LANGUAGES.map((lang: { code: string; name: string; flag: string }) => (
                          <MenuItem key={lang.code} value={lang.code}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography>{lang.flag}</Typography>
                              <Typography>{lang.name}</Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Theme Toggle */}
                  <IconButton
                    onClick={onThemeToggle}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                    title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                  >
                    {isDarkMode ? <LightMode /> : <DarkMode />}
                  </IconButton>

                  {/* Notifications */}
                  {showNotifications && (
                    <IconButton
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <Badge badgeContent={3} color="error">
                        <Notifications />
                      </Badge>
                    </IconButton>
                  )}

                  {/* User Menu */}
                  {variant === 'authenticated' && userName && (
                    <Chip
                      icon={<AccountCircle />}
                      label={userName}
                      onClick={onProfileClick}
                      {...(onLogout && {
                        onDelete: onLogout,
                        deleteIcon: <Logout fontSize="small" />,
                      })}
                      sx={{
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiChip-deleteIcon': {
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            color: theme.palette.error.main,
                          },
                        },
                      }}
                    />
                  )}

                  {/* Support Button */}
                  <Button
                    variant={variant === 'public' ? 'contained' : 'outlined'}
                    startIcon={<LiveHelp />}
                    sx={{
                      fontWeight: 600,
                      boxShadow: 'none',
                      '&:hover': {
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    Support Center
                  </Button>
                </Stack>
              )}

              {/* Mobile Menu Button */}
              {isTablet && (
                <IconButton
                  edge="end"
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
          </Container>
        </MuiAppBar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 300,
              maxWidth: '80%',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={700}>
                Menu
              </Typography>
              <IconButton onClick={() => setMobileMenuOpen(false)}>
                <Close />
              </IconButton>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* User Info */}
            {variant === 'authenticated' && userName && (
              <>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {userName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {userRole}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
              </>
            )}

            <List>
              {/* Theme Toggle */}
              <ListItem disablePadding>
                <ListItemButton onClick={onThemeToggle}>
                  <ListItemIcon>{isDarkMode ? <LightMode /> : <DarkMode />}</ListItemIcon>
                  <ListItemText primary={isDarkMode ? 'Light Mode' : 'Dark Mode'} />
                </ListItemButton>
              </ListItem>

              {showNotifications && (
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemIcon>
                      <Badge badgeContent={3} color="error">
                        <Notifications />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary="Notifications" />
                  </ListItemButton>
                </ListItem>
              )}

              {variant === 'authenticated' && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={onProfileClick}>
                      <ListItemIcon>
                        <AccountCircle />
                      </ListItemIcon>
                      <ListItemText primary="Profile" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton>
                      <ListItemIcon>
                        <Settings />
                      </ListItemIcon>
                      <ListItemText primary="Settings" />
                    </ListItemButton>
                  </ListItem>
                </>
              )}

              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon>
                    <Support />
                  </ListItemIcon>
                  <ListItemText primary="Support Center" />
                </ListItemButton>
              </ListItem>

              {variant === 'authenticated' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <ListItem disablePadding>
                    <ListItemButton onClick={onLogout} sx={{ color: theme.palette.error.main }}>
                      <ListItemIcon>
                        <Logout color="error" />
                      </ListItemIcon>
                      <ListItemText primary="Logout" />
                    </ListItemButton>
                  </ListItem>
                </>
              )}
            </List>
          </Box>
        </Drawer>

        {/* Toolbar spacer */}
        <Toolbar sx={{ height: { xs: 56, sm: trigger ? 60 : 64 } }} />
      </>
    )
  }
)

AppBar.displayName = 'AppBar'

export default AppBar
